import { invocarFuncao } from "@/lib/edgeFunction";

/**
 * Funções de seguradora (29/10/2026): o que qualquer seguradora pode fazer na
 * plataforma e o que cada uma declara. A tela mostra o botão pela função; toda
 * chamada passa pela porta única (edge function `seguradoras`), que só
 * encaminha função declarada.
 *
 * Espelho de db/supabase/functions/_shared/seguradoras/core/funcoes.ts (manter igual).
 */

export const FUNCOES = {
  cotar: "Cotação",
  emitir: "Emissão de proposta",
  declaracao_saude: "Declaração de saúde",
  pagamento: "Pagamento",
  assinatura: "Assinatura",
  documentos: "Documentos",
  acompanhar: "Acompanhamento",
  exigencias: "Exigências da seguradora",
  propostas: "Gestão de propostas",
  cotacoes_salvas: "Cotações salvas",
  vencimento: "Troca de vencimento",
  endosso: "Endosso",
  comissoes: "Comissões",
  nota_fiscal: "Nota fiscal",
  sinistro: "Sinistro",
  leads: "Leads",
  campanhas: "Campanhas",
} as const;

export type Funcao = keyof typeof FUNCOES;

export const SEGURADORAS_FUNCOES: Record<string, readonly Funcao[]> = {
  mag: ["cotar", "emitir", "declaracao_saude", "pagamento", "documentos", "acompanhar", "exigencias", "comissoes"],
  azos: [
    "cotar",
    "emitir",
    "declaracao_saude",
    "pagamento",
    "assinatura",
    "documentos",
    "acompanhar",
    "exigencias",
    "propostas",
    "cotacoes_salvas",
    "vencimento",
    "endosso",
    "comissoes",
    "nota_fiscal",
    "leads",
    "campanhas",
  ],
};

export const temFuncao = (seguradora: string | null | undefined, funcao: Funcao) =>
  !!seguradora && (SEGURADORAS_FUNCOES[seguradora] ?? []).includes(funcao);

/** Chamada pela porta única: `pedido` é o que o adaptador da seguradora entende. */
export const chamarSeguradora = <T>(
  seguradora: string,
  funcao: Funcao,
  pedido: Record<string, unknown>,
  quando: string,
) => invocarFuncao<T>("seguradoras", { body: { seguradora, funcao, pedido }, quando });

/** Ação do adaptador da MAG (seguradoras-mag-proposta) → função. Espelho de _shared/seguradoras/porta.ts. */
const FUNCAO_MAG: Record<string, Funcao> = {
  registrar: "emitir",
  questionarios: "declaracao_saude",
  documento: "documentos",
  pendencias: "exigencias",
  responder: "exigencias",
  situacao: "acompanhar",
  cartao: "pagamento",
};

/** Pedido do adaptador da MAG pela porta, com a função tirada da ação. */
export const chamarMag = <T>(pedido: Record<string, unknown>, quando: string) =>
  chamarSeguradora<T>("mag", FUNCAO_MAG[String(pedido.acao ?? "registrar")] ?? "emitir", pedido, quando);

/** Passo da emissão pelo cotador da Azos → função. Espelho de azos/cotador_emissao.ts (FUNCAO_DO_PASSO). */
export const FUNCAO_EMISSAO_AZOS = {
  cotar: "cotar",
  coberturas: "emitir",
  saude: "declaracao_saude",
  segurado: "emitir",
  pagamento: "pagamento",
  assinatura: "assinatura",
} as const satisfies Record<string, Funcao>;

export type PassoEmissaoAzos = keyof typeof FUNCAO_EMISSAO_AZOS;

/** Um passo da emissão da Azos pela porta. */
export const chamarEmissaoAzos = <T>(passo: PassoEmissaoAzos, dados: Record<string, unknown>, quando: string) =>
  chamarSeguradora<T>("azos", FUNCAO_EMISSAO_AZOS[passo], { acao: "emissao", passo, dados }, quando);
